import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Calendar, Activity } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { RiskBadge } from './RiskBadge';
import { cn } from '../../lib/utils';

export function PatientListItem({ 
  patient, 
  onClick, 
  showActions = false,
  actions = [],
  index = 0 
}) {
  const { name, avatar, condition, riskLevel, compliance, nextSession, serviceType } = patient;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: index * 0.03 }}
      className="group flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:shadow-elevated transition-shadow duration-200 cursor-pointer"
      onClick={onClick}
    >
      <Avatar className="w-11 h-11">
        <AvatarImage src={avatar} />
        <AvatarFallback>{name?.split(' ').map(n => n[0]).join('') || '?'}</AvatarFallback>
      </Avatar>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-medium text-foreground truncate">{name}</h4>
          <RiskBadge level={riskLevel} size="sm" />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Activity className="w-3 h-3" />
            {condition}
          </span>
          {serviceType && (
            <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
              {serviceType}
            </Badge>
          )}
        </div>
      </div>
      
      <div className="hidden sm:flex flex-col items-end gap-1">
        {compliance !== undefined && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Compliance</span>
            <span className={cn(
              'text-sm font-semibold',
              compliance >= 80 ? 'text-success' : compliance >= 60 ? 'text-warning' : 'text-primary'
            )}>
              {compliance}%
            </span>
          </div>
        )}
        {nextSession && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {nextSession}
          </span>
        )}
      </div>
      
      {showActions && actions.length > 0 ? (
        <div className="flex gap-2">
          {actions.map((action, idx) => (
            <Button
              key={idx}
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick?.();
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      ) : (
        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
      )}
    </motion.div>
  );
}

export function PatientQuickAction({ label, icon: Icon, onClick, variant = 'default' }) {
  return (
    <Button
      variant={variant === 'primary' ? 'default' : 'outline'}
      size="sm"
      className="text-xs h-8"
      onClick={onClick}
    >
      {Icon && <Icon className="w-3 h-3 mr-1.5" />}
      {label}
    </Button>
  );
}
